 import React, { useRef, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  ShoppingCartOutlined,
  SearchOutlined,
  UserOutlined,
  WhatsAppOutlined,
  PhoneOutlined,
  MessageOutlined,
  ShoppingOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Badge, message } from 'antd';
import Header from './Mudules/Header';
import Footer from './Mudules/Footer';
import Home from './Mudules/Home';
import ProductDetails from './components/ProductDetails';
import './App.css';
import Basket from './Mudules/Basket';
import Product from './Mudules/Product';
import Discount from './Mudules/Discount';
import Contact from './Mudules/Contact';
import About from './Mudules/About';
import Faq from './Mudules/Faq';
import Checkout from './Mudules/Checkout';
import Login from './Mudules/Login';
import { useAuth } from './components/AuthContext';
import Imports from './Mudules/Imports';
import Blog from './Mudules/Blog';
import Blogdetail from './Mudules/Blogdetail';
import Local from './Mudules/Local';
import AIAssistant from './components/AIAssistant';

// ScrollToTop component – resets scroll on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  const [loading, setLoading] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const [unreadMessages] = useState(3);
  const navbarRef = useRef(null);
  const chatModalRef = useRef(null);
  const whatsappButtonRef = useRef(null);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [hasSpokenWelcome, setHasSpokenWelcome] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  // Hide floating buttons on /ai and /checkout
  const hideFloatingButtons = location.pathname === '/ai' || location.pathname === '/checkout';

  // Updated welcome message text - End of Year Sale ONLY
  const welcomeSpeechText = `🎉 End of Year Sale is Here! 🎉 Get up to 30 percent off on selected furniture. Don't miss this opportunity to transform your home before the end of the year. 

I am Dollar Furniture AI, your personal shopping assistant. Here's how to use me:
• Budget Planning: Say "I have 100,000 and need a bed, sofa and dining table"
• Product Search: Say "Show me sofas under 50,000"
• Shopping Cart: Say "Add The Dollar Furniture Bed to my cart"
• Place Order: Say "I want to place an order"
• Company Info: Ask about return policy, showroom location, delivery, warranty.

To interact with me, please click on the AI Assistant icon with my face. That will take you to my page where I can chat with me and I will help you shop. To turn off my voice, open the AI assistant page and use the "AI Voice" toggle switch. Enjoy shopping with Dollar Furniture!`;

  // Get female voice only
  const getFemaleVoice = (voices) => {
    const femaleVoiceNames = [
      'Google UK English Female',
      'Google US English Female', 
      'Samantha',
      'Microsoft Zira',
      'Karen',
      'Moira',
      'Tessa',
      'Veena',
      'Victoria'
    ];
    
    for (const name of femaleVoiceNames) {
      const voice = voices.find(v => v.name.includes(name));
      if (voice) return voice;
    }
    
    const femaleVoice = voices.find(v => 
      v.name.toLowerCase().includes('female') || 
      v.name.toLowerCase().includes('woman') ||
      v.name.toLowerCase().includes('girl')
    );
    
    if (femaleVoice) return femaleVoice;
    
    return voices[0];
  };

  // Function to speak the welcome message
  const speakWelcomeMessage = () => {
    // Prevent multiple triggers
    if (hasSpokenWelcome) return;
    
    const speechSynth = window.speechSynthesis;
    
    if (!speechSynth) {
      message.error('Your browser does not support speech synthesis.');
      setHasSpokenWelcome(true);
      setShowOverlay(false);
      return;
    }
    
    // Cancel any ongoing speech
    speechSynth.cancel();
    
    const utterance = new SpeechSynthesisUtterance(welcomeSpeechText);
    
    const setVoiceAndSpeak = () => {
      const voices = speechSynth.getVoices();
      const femaleVoice = getFemaleVoice(voices);
      if (femaleVoice) utterance.voice = femaleVoice;
      utterance.rate = 0.9;
      utterance.pitch = 1.2;
      utterance.lang = 'en-US';
      utterance.onend = () => {
        console.log('Welcome message finished');
      };
      utterance.onerror = (e) => {
        console.error('Speech error:', e);
      };
      speechSynth.speak(utterance);
    };
    
    if (speechSynth.getVoices().length > 0) {
      setVoiceAndSpeak();
    } else {
      speechSynth.onvoiceschanged = setVoiceAndSpeak;
    }
    
    setHasSpokenWelcome(true);
    setShowOverlay(false);
    sessionStorage.setItem('dollar_furniture_welcome_spoken', 'true');
    message.success('🎤 AI Assistant is speaking...');
  };

  // Handle overlay click – invisible overlay triggers voice
  const handleOverlayClick = () => {
    if (!hasSpokenWelcome && showOverlay) {
      speakWelcomeMessage();
    }
  };

  // Handle scroll trigger
  useEffect(() => {
    const handleScroll = () => {
      if (!hasSpokenWelcome && showOverlay) {
        speakWelcomeMessage();
      }
    };
    
    if (showOverlay && !hasSpokenWelcome) {
      window.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showOverlay, hasSpokenWelcome]);

  // Check if we've already spoken in this session
  useEffect(() => {
    const alreadySpoken = sessionStorage.getItem('dollar_furniture_welcome_spoken');
    if (alreadySpoken) {
      setHasSpokenWelcome(true);
      setShowOverlay(false);
    }
  }, []);

  // Preload voices
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
    }
  }, []);

  // SEO optimization
  useEffect(() => {
    if (window.location.pathname === '/products') {
      document.title = 'Furniture Products in Nairobi - Sofas, Beds, Office Desks | Dollar Furniture';
      document.querySelector('meta[name="description"]')?.setAttribute('content', 
        'Browse our complete furniture catalog. Shop sofas from KSh 12,000, beds from KSh 8,000, office desks from KSh 5,500. Free delivery in Nairobi.'
      );
    }
  }, []);
  
  // Measure navbar height
  useEffect(() => {
    if (!navbarRef.current) return;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setNavbarHeight(entry.contentRect.height);
      }
    });
    resizeObserver.observe(navbarRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Close chat modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatModalRef.current && !chatModalRef.current.contains(event.target) &&
          whatsappButtonRef.current && !whatsappButtonRef.current.contains(event.target)) {
        setChatVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [location]);

  const toggleChat = () => setChatVisible(prev => !prev);

  const openWhatsApp = (message = '') => {
    const phoneNumber = '+254729335354';
    const defaultMessage = "Hello Dollar Furniture Kenya! I'd like to inquire about your products.";
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message || defaultMessage)}`;
    window.open(url, '_blank');
    setChatVisible(false);
  };

  const openYouTube = () => {
    window.open('https://www.youtube.com/channel/UChQXye55FCbuSfL5gHvksDA', '_blank');
    setChatVisible(false);
  };

  const handleQuickAction = (action, e) => {
    e.preventDefault();
    e.stopPropagation();
    let message = '';
    switch (action) {
      case 'order':
        message = "Hello Dollar Furniture Kenya! I'd like to place an order. Can you assist me?";
        break;
      case 'catalog':
        message = "Hello Dollar Furniture Kenya! Can you send me your latest furniture catalog?";
        break;
      case 'support':
        message = "Hello Dollar Furniture Kenya! I need support with my order.";
        break;
      default:
        message = "Hello Dollar Furniture Kenya! I need assistance.";
    }
    openWhatsApp(message);
  };

  const navigateToAI = () => {
    window.location.href = '/ai';
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <style>
        {`
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
          @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          @keyframes scale-in {
            0% { opacity: 0; transform: scale(0.95); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes gentle-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.4); }
            50% { box-shadow: 0 0 0 12px rgba(24, 144, 255, 0); }
          }
          .animate-bounce-slow {
            animation: bounce-slow 2s ease-in-out infinite;
          }
          .animate-pulse-slow {
            animation: pulse-slow 1.5s ease-in-out infinite;
          }
          .animate-scale-in {
            animation: scale-in 0.2s ease-out;
          }
          .ai-pulse {
            animation: gentle-pulse 2s ease-in-out infinite;
          }
        `}
      </style>

      {/* COMPLETELY TRANSPARENT OVERLAY - only captures clicks, no visible content */}
      {showOverlay && !hideFloatingButtons && (
        <div
          onClick={handleOverlayClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            zIndex: 999999,
            cursor: 'pointer',
          }}
        />
      )}

      <Header ref={navbarRef} />
      <ScrollToTop />

      <main style={{ flex: 1, paddingTop: navbarHeight, marginTop: 0 }}>
        {loading ? (
          <div className="loading-container">
            <div className="animated-circle"></div>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/product-details/:productId" element={<ProductDetails />} />
            <Route path="/blogdetail" element={<Blogdetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/basket" element={<Basket />} />
            <Route path="/product" element={<Product />} />
            <Route path="/discount" element={<Discount />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/imports" element={<Imports />} />
            <Route path="/login" element={<Login />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/local" element={<Local />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/ai" element={<AIAssistant />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        )}
      </main>

      {/* Floating Buttons Container – hidden on /ai and /checkout and when overlay is visible */}
      {!hideFloatingButtons && !showOverlay && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '16px',
        }}>
          {/* AI Assistant Button */}
          <div
            onClick={navigateToAI}
            className="ai-pulse"
            style={{
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <img
              src="https://firebasestorage.googleapis.com/v0/b/dollar-eda6d.firebasestorage.app/o/dollar%2FChatGPT%20Image%20May%207%2C%202026%2C%2012_04_20%20AM.png?alt=media&token=464a90c8-9b9c-449d-99e7-a64cec245f9f"
              alt="Dollar Furniture AI Assistant"
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                borderRadius: '50%',
                backgroundColor: 'white',
                padding: '4px',
              }}
            />
            <span style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#1890ff',
              background: 'white',
              padding: '2px 8px',
              borderRadius: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>AI Assistant</span>
          </div>

          {/* YouTube Button */}
          <button
            onClick={openYouTube}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF0000, #CC0000)',
              color: 'white',
              boxShadow: '0 10px 25px -5px rgba(255,0,0,0.4)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              border: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.08)';
              e.currentTarget.style.boxShadow = '0 15px 30px -5px rgba(255,0,0,0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(255,0,0,0.4)';
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="28px" height="28px">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.376.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.376-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </button>

          {/* WhatsApp Button */}
          <button
            ref={whatsappButtonRef}
            onClick={toggleChat}
            className="animate-bounce-slow"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #25D366, #128C7E)',
              color: 'white',
              boxShadow: '0 10px 25px -5px rgba(37,211,102,0.4)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              border: 'none',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.08)';
              e.currentTarget.style.boxShadow = '0 15px 30px -5px rgba(37,211,102,0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(37,211,102,0.4)';
            }}
          >
            <Badge count={unreadMessages} size="small" offset={[-2, 5]} style={{ backgroundColor: '#ff4d4f' }}>
              <WhatsAppOutlined style={{ fontSize: '28px' }} />
            </Badge>
          </button>
        </div>
      )}

      {/* Chat Modal */}
      {chatVisible && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setChatVisible(false);
          }}
        >
          <div
            ref={chatModalRef}
            className="animate-scale-in"
            style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              width: '100%',
              maxWidth: '420px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1890ff, #096dd9)',
              padding: '20px',
              color: 'white',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <WhatsAppOutlined style={{ fontSize: '24px', color: 'white' }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '18px' }}>Dollar Furniture Support</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>Furniture Experts</p>
                  </div>
                </div>
                <button
                  onClick={() => setChatVisible(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                    borderRadius: '50%',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              backgroundColor: '#f0f2f5',
            }}>
              {/* Welcome message */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #25D366, #128C7E)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>DF</span>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '18px',
                  borderTopLeftRadius: '4px',
                  padding: '12px 16px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  maxWidth: '80%',
                }}>
                  <p style={{ margin: 0, color: '#333', fontSize: '14px', lineHeight: 1.5 }}>
                    Hello! Welcome to Dollar Furniture Kenya. How can we assist you with furniture purchases, delivery, or support?
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#25D366', borderRadius: '50%', animation: 'pulse-slow 1.5s infinite' }} />
                    <span style={{ fontSize: '11px', color: '#888' }}>Online now</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '12px' }}>Quick Actions:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    onClick={(e) => handleQuickAction('order', e)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      border: '1px solid #e6f0ff',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ width: '40px', height: '40px', backgroundColor: '#e6f0ff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ShoppingOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1890ff' }}>Order Furniture</div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>Place a new order or request custom furniture</div>
                    </div>
                  </button>

                  <button
                    onClick={(e) => handleQuickAction('catalog', e)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      border: '1px solid #f0f7e6',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f6ffed'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ width: '40px', height: '40px', backgroundColor: '#f6ffed', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '20px' }}>📋</span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#52c41a' }}>Request Catalog</div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>Get our latest furniture catalog</div>
                    </div>
                  </button>

                  <button
                    onClick={(e) => handleQuickAction('support', e)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      border: '1px solid #fff0e6',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff7f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ width: '40px', height: '40px', backgroundColor: '#fff0e6', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <MessageOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#fa8c16' }}>Order Support</div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>Help with existing orders or deliveries</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* AI Assistant Promotion */}
              <div style={{
                backgroundColor: '#e6f7ff',
                borderRadius: '16px',
                padding: '16px',
                border: '1px solid #91d5ff',
                marginBottom: '16px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '10px', color: '#0958d9' }}>
                  🤖 Try Our AI Assistant!
                </div>
                <div style={{ fontSize: '13px', color: '#0050b3', marginBottom: '12px' }}>
                  Get instant help with product recommendations, budget planning, and order placement.
                </div>
                <button
                  onClick={navigateToAI}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#1890ff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <span>👩</span> Chat with AI Assistant
                </button>
              </div>

              {/* Direct Contact Info */}
              <div style={{
                backgroundColor: '#f9f9f9',
                borderRadius: '16px',
                padding: '16px',
                border: '1px solid #e8e8e8',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '10px', color: '#333' }}>Direct Contact:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                    <span style={{ fontSize: '13px' }}>Call: <strong>+254 729 335 354</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <WhatsAppOutlined style={{ color: '#25D366', fontSize: '16px' }} />
                    <span style={{ fontSize: '13px' }}>WhatsApp: <strong>+254 729 335 354</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>✉️</span>
                    <span style={{ fontSize: '13px' }}>Email: <strong>mwauraj118@gmail.com</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📸</span>
                    <span style={{ fontSize: '13px' }}>Instagram: <strong>@dollar_furniture_ke</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>▶️</span>
                    <span style={{ fontSize: '13px' }}>YouTube: <strong>Dollar Furniture Kenya</strong></span>
                    <button
                      onClick={openYouTube}
                      style={{
                        marginLeft: 'auto',
                        background: '#FF0000',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '2px 10px',
                        color: 'white',
                        fontSize: '11px',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      Watch
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: 'white',
            }}>
              <button
                onClick={() => openWhatsApp()}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #25D366, #128C7E)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '40px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(37,211,102,0.3)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <WhatsAppOutlined style={{ fontSize: '20px' }} />
                Open WhatsApp
              </button>
              <p style={{ textAlign: 'center', fontSize: '11px', color: '#888', marginTop: '12px', marginBottom: 0 }}>
                Response time: Usually within minutes
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <div className="bottom-nav" style={{ padding: '8px 0' }}>
        <a href="/" className="nav-item" style={{ fontSize: '12px' }}>
          <HomeOutlined style={{ fontSize: '20px' }} />
          <span style={{ fontSize: '12px' }}>Home</span>
        </a>
        <a href="/basket" className="nav-item" style={{ fontSize: '12px' }}>
          <ShoppingCartOutlined style={{ fontSize: '20px' }} />
          <span style={{ fontSize: '12px' }}>Cart</span>
        </a>
        <a href="/product" className="nav-item" style={{ fontSize: '12px' }}>
          <SearchOutlined style={{ fontSize: '20px' }} />
          <span style={{ fontSize: '12px' }}>Search</span>
        </a>
        <a href="/login" className="nav-item" style={{ fontSize: '12px' }}>
          <UserOutlined style={{ fontSize: '20px' }} />
          <span style={{ fontSize: '12px' }}>Profile</span>
        </a>
      </div>

      <Footer />
    </div>
  );
}

export default App;